"use client";

import React, { useEffect, useState } from "react";
import { Table, Spinner, Card, Badge, Button, Modal } from "react-bootstrap";
import { TariffInfo, TariffPriceInfo } from "./types";
import { useLicense } from "../../hooks/useLicense";
import { useLimitCounts } from "../../storage/accessors";
import Icon from "../../components/Icon";
import { faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import { getCsrfToken } from "../../components/utils/csrf";
import { UserContext } from "../../components/auth/auth";
import { useContext } from "react";

interface TariffsTableProps {
    className?: string;
    exceededLimits: string[];
}

type LimitKey = 'limit_life_part' | 'limit_trackers' | 'limit_lists' | 'limit_tasks' | 'limit_checklists';

export const TariffsTable: React.FC<TariffsTableProps> = ({ className, exceededLimits }) => {
    const [tariffs, setTariffs] = useState<TariffInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { licenseInfo } = useLicense();
    const currentPlanId = licenseInfo?.planId;
    const currentCounts = useLimitCounts();
    const { loadUser } = useContext(UserContext);

    // Состояние для модального окна активации промо
    const [promoModal, setPromoModal] = useState<{ show: boolean; tariffId: number | null }>({
        show: false,
        tariffId: null,
    });
    const [activating, setActivating] = useState(false);
    const [activationResult, setActivationResult] = useState<{ success: boolean; message: string } | null>(null);

    useEffect(() => {
        const fetchTariffs = async () => {
            try {
                const response = await fetch("/api/tariffs");
                if (response.ok) {
                    const data: TariffInfo[] = await response.json();
                    setTariffs(data);
                } else if (response.status === 401) {
                    setError("Для просмотра тарифов необходимо авторизоваться");
                } else {
                    setError("Не удалось загрузить тарифы");
                }
            } catch {
                setError("Ошибка сети. Попробуйте позже.");
            } finally {
                setLoading(false);
            }
        };

        fetchTariffs();
    }, []);

    const handleActivatePromo = async () => {
        if (!promoModal.tariffId) return;

        setActivating(true);
        setActivationResult(null);

        try {
            const csrfToken = getCsrfToken();
            const response = await fetch("/api/auth/enable-promo", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Csrftoken": csrfToken,
                },
                body: JSON.stringify({ tariff_id: promoModal.tariffId }),
            });

            const data = await response.json();

            if (data.success) {
                setActivationResult({ success: true, message: "Промо успешно активировано!" });
                // Возврат на предыдущую страницу и обновление данных пользователя
                setTimeout(async () => {
                    await loadUser(true);
                    window.history.back();
                }, 1500);
            } else {
                setActivationResult({ success: false, message: data.message || "Не удалось активировать промо" });
            }
        } catch {
            setActivationResult({ success: false, message: "Ошибка сети. Попробуйте позже." });
        } finally {
            setActivating(false);
        }
    };

    const closePromoModal = () => {
        setPromoModal({ show: false, tariffId: null });
        setActivationResult(null);
    };

    const formatCost = (cost: number) => {
        return new Intl.NumberFormat("ru-RU", {
            style: "currency",
            currency: "RUB",
            minimumFractionDigits: 0,
        }).format(cost);
    };

    const renderPrices = (prices: TariffPriceInfo[]) => {
        if (!prices || prices.length === 0) return null;
        return (
            <div className="text-muted small">
                {prices.map((price) => (
                    <div key={price.id}>
                        {formatCost(price.price)} — {price.title}
                    </div>
                ))}
            </div>
        );
    };

    const formatLimit = (limit: number) => {
        if (limit === 999999) return "безлимит";
        return limit.toString();
    };

    const getCountForLimit = (limitKey: LimitKey): number => {
        const countMap: Record<LimitKey, keyof typeof currentCounts> = {
            'limit_life_part': 'lifeParts',
            'limit_trackers': 'trackers',
            'limit_lists': 'taskLists',
            'limit_tasks': 'tasks',
            'limit_checklists': 'checklists',
        };
        return (currentCounts as any)[countMap[limitKey]] || 0;
    };

    const renderLimitCell = (tariff: TariffInfo, limitKey: LimitKey) => {
        const limitValue = tariff[limitKey] as number;
        const currentCount = getCountForLimit(limitKey);
        const isOverLimit = currentCount >= limitValue && limitValue < 999999;
        const isCurrentTariff = currentPlanId === tariff.id;

        return (
            <td key={tariff.id}>
                <div>
                    <strong>{formatLimit(limitValue)}</strong>
                    {isCurrentTariff && (
                        <div className="text-muted small">
                            текущее: <strong className={isOverLimit ? "text-danger" : ""}>{currentCount}</strong>
                        </div>
                    )}
                </div>
            </td>
        );
    };

    return (
        <div className={className}>
            {/* Десктопная таблица */}
            <Card className="mb-4 d-none d-md-block">
                <Card.Body className="p-0">
                    <Table striped bordered hover responsive className="mb-0 text-center">
                        <thead>
                            <tr>
                                <th className="text-start">Параметр</th>
                                {tariffs.map((tariff) => (
                                    <th key={tariff.id} className="align-top" style={{ minWidth: "150px" }}>
                                        <div className="fw-bold">{tariff.name}</div>
                                        {currentPlanId === tariff.id && (
                                            <Badge bg="success" className="mb-1">Текущий</Badge>
                                        )}
                                        {renderPrices(tariff.prices)}
                                        {tariff.promo_period_days && (
                                            <div className="text-success fw-semibold mt-1">
                                                Акция: {tariff.promo_period_days} дней бесплатно
                                            </div>
                                        )}
                                        {tariff.promo_period_days && tariff.promo_period_days > 0 && currentPlanId !== tariff.id && (
                                            <div className="mt-2">
                                                <Button
                                                    variant="outline-success"
                                                    size="sm"
                                                    onClick={() => setPromoModal({ show: true, tariffId: tariff.id })}
                                                >
                                                    Активировать
                                                </Button>
                                            </div>
                                        )}
                                        {tariff.promo_period_days && tariff.promo_period_days > 0 && currentPlanId === tariff.id && (
                                            <div className="mt-2">
                                                <Badge bg="success">Активно</Badge>
                                            </div>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="text-start fw-semibold">Частей в балансе</td>
                                {tariffs.map((tariff) => renderLimitCell(tariff, 'limit_life_part'))}
                            </tr>
                            <tr>
                                <td className="text-start fw-semibold">Трекеры</td>
                                {tariffs.map((tariff) => renderLimitCell(tariff, 'limit_trackers'))}
                            </tr>
                            <tr>
                                <td className="text-start fw-semibold">Списки</td>
                                {tariffs.map((tariff) => renderLimitCell(tariff, 'limit_lists'))}
                            </tr>
                            <tr>
                                <td className="text-start fw-semibold">Задачи</td>
                                {tariffs.map((tariff) => renderLimitCell(tariff, 'limit_tasks'))}
                            </tr>
                            <tr>
                                <td className="text-start fw-semibold">Чек-листы</td>
                                {tariffs.map((tariff) => renderLimitCell(tariff, 'limit_checklists'))}
                            </tr>
                            <tr>
                                <td className="text-start fw-semibold">API для трекеров</td>
                                {tariffs.map((tariff) => (
                                    <td key={tariff.id}>
                                        {!tariff.limit_tracker_api ? (
                                            <span className="text-success">✓ доступно</span>
                                        ) : (
                                            <span className="text-muted">✕ недоступно</span>
                                        )}
                                    </td>
                                ))}
                            </tr>
                            <tr>
                                <td className="text-start fw-semibold">Оффлайн режим (дней)</td>
                                {tariffs.map((tariff) => (
                                    <td key={tariff.id}>{tariff.limit_offline_days}</td>
                                ))}
                            </tr>
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
            {/* Мобильные карточки */}
            <div className="d-md-none">
                {tariffs.map((tariff) => (
                    <Card key={tariff.id} className={`mb-3`}>
                        <Card.Header className="text-center">
                            <div className="fw-bold d-flex align-items-center justify-content-center gap-2">
                                {tariff.name}
                                {currentPlanId === tariff.id && (
                                    <Badge bg="success">Текущий</Badge>
                                )}
                            </div>
                            {renderPrices(tariff.prices)}
                            {tariff.promo_period_days && (
                                <div className="text-success fw-semibold mt-2">
                                    Акция: {tariff.promo_period_days} дней бесплатно
                                </div>
                            )}
                            {tariff.promo_period_days && tariff.promo_period_days > 0 && currentPlanId !== tariff.id && (
                                <div className="mt-2">
                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        onClick={() => setPromoModal({ show: true, tariffId: tariff.id })}
                                    >
                                        Активировать
                                    </Button>
                                </div>
                            )}
                            {tariff.promo_period_days && tariff.promo_period_days > 0 && currentPlanId === tariff.id && (
                                <div className="mt-2">
                                    <Badge bg="success">Активно</Badge>
                                </div>
                            )}
                        </Card.Header>
                        <Card.Body>
                            <Table size="sm" className="mb-0">
                                <tbody>
                                    <tr>
                                        <td className="text-start fw-semibold">Частей в балансе</td>
                                        <td className="text-end">
                                            <strong>{formatLimit(tariff.limit_life_part)}</strong>
                                            {currentPlanId === tariff.id && (
                                                <div className="text-muted small">текущее: <strong className={currentCounts.lifeParts >= tariff.limit_life_part && tariff.limit_life_part < 999999 ? "text-danger" : ""}>{currentCounts.lifeParts}</strong></div>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-start fw-semibold">Трекеры</td>
                                        <td className="text-end">
                                            <strong>{formatLimit(tariff.limit_trackers)}</strong>
                                            {currentPlanId === tariff.id && (
                                                <div className="text-muted small">текущее: <strong className={currentCounts.trackers >= tariff.limit_trackers && tariff.limit_trackers < 999999 ? "text-danger" : ""}>{currentCounts.trackers}</strong></div>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-start fw-semibold">Списки</td>
                                        <td className="text-end">
                                            <strong>{formatLimit(tariff.limit_lists)}</strong>
                                            {currentPlanId === tariff.id && (
                                                <div className="text-muted small">текущее: <strong className={currentCounts.taskLists >= tariff.limit_lists && tariff.limit_lists < 999999 ? "text-danger" : ""}>{currentCounts.taskLists}</strong></div>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-start fw-semibold">Задачи</td>
                                        <td className="text-end">
                                            <strong>{formatLimit(tariff.limit_tasks)}</strong>
                                            {currentPlanId === tariff.id && (
                                                <div className="text-muted small">текущее: <strong className={currentCounts.tasks >= tariff.limit_tasks && tariff.limit_tasks < 999999 ? "text-danger" : ""}>{currentCounts.tasks}</strong></div>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-start fw-semibold">Чек-листы</td>
                                        <td className="text-end">
                                            <strong>{formatLimit(tariff.limit_checklists)}</strong>
                                            {currentPlanId === tariff.id && (
                                                <div className="text-muted small">текущее: <strong className={currentCounts.checklists >= tariff.limit_checklists && tariff.limit_checklists < 999999 ? "text-danger" : ""}>{currentCounts.checklists}</strong></div>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-start fw-semibold">API для трекеров</td>
                                        <td className="text-end">
                                            {!tariff.limit_tracker_api ? (
                                                <span className="text-success">✓ доступно</span>
                                            ) : (
                                                <span className="text-muted">✕ недоступно</span>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-start fw-semibold">Оффлайн режим (дней)</td>
                                        <td className="text-end">{tariff.limit_offline_days}</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                ))}
            </div>
            {/* Модальное окно активации промо */}
            <Modal show={promoModal.show} onHide={closePromoModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Активация промо-тарифа</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {activationResult ? (
                        <div className={`text-center ${activationResult.success ? "text-success" : "text-danger"}`}>
                            {activationResult.success ? (
                                <Icon icon={faCheck} size="3x" className="mb-3" />
                            ) : (
                                <Icon icon={faXmark} size="3x" className="mb-3" />
                            )}
                            <p className="mb-0">{activationResult.message}</p>
                        </div>
                    ) : (
                        <p>
                            Вы хотите активировать промо-предложение и получить{" "}
                            <strong>
                                {tariffs.find(t => t.id === promoModal.tariffId)?.promo_period_days} дней бесплатно
                            </strong> на тарифе <strong>"{tariffs.find(t => t.id === promoModal.tariffId)?.name}"</strong>?
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {activationResult ? (
                        <Button variant="primary" onClick={closePromoModal}>
                            Закрыть
                        </Button>
                    ) : (
                        <>
                            <Button variant="secondary" onClick={closePromoModal} disabled={activating}>
                                Отмена
                            </Button>
                            <Button variant="outline-success" onClick={handleActivatePromo} disabled={activating}>
                                {activating && <Spinner animation="border" size="sm" className="me-2" />}
                                {activating ? "Активация..." : "Активировать"}
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};